import {
  type Event,
  type Organisation,
  type RunState,
  type TriageCategory,
  type UnitStatus,
  signById,
} from '@orgsim/shared';

export const UNIT_STATUS_LABELS: Record<UnitStatus, string> = {
  alerted: 'Alarmiert',
  onScene: 'Vor Ort',
  requested: 'Angefordert',
  arrived: 'Eingetroffen',
  departed: 'Abgerückt',
};

export const ORGANISATION_LABELS: Record<Organisation, string> = {
  hilfsorganisation: 'Rettungsdienst',
  feuerwehr: 'Feuerwehr',
};

export const CATEGORY_LABELS: Record<TriageCategory, string> = {
  I: 'SK I (rot), sofortige Behandlung',
  II: 'SK II (gelb), dringende Behandlung',
  III: 'SK III (grün), spätere Behandlung',
  IV: 'SK IV (blau), betreuende Behandlung',
  V: 'SK V (schwarz), keine Behandlung',
};

export const EVENT_LABELS: Record<Event['type'], string> = {
  SignPlaced: 'Zeichen platziert',
  SignMoved: 'Zeichen verschoben',
  SignRemoved: 'Zeichen entfernt',
  UnitStatusChanged: 'Status geändert',
  ReinforcementRequested: 'Nachforderung',
  ReinforcementArrived: 'Nachforderung eingetroffen',
  PatientTriaged: 'Patient gesichtet',
  PatientTransferred: 'Patient verlegt',
  SurgicalIndicationSet: 'OP-Indikation',
  TransportReleaseSet: 'Transportfreigabe',
  PatientAdded: 'Patient hinzugefügt',
  InjectTriggered: 'Einspieler',
  DrawingAdded: 'Zeichnung hinzugefügt',
  DrawingRemoved: 'Zeichnung entfernt',
  NoteAdded: 'Notiz',
};

// The transfer destinations the Vorgabe names, shared between the panel's buttons and the
// protocol so both use the same words.
export const TRANSFER_DESTINATIONS = [
  'Leichtverletzten-Behandlungsplatz',
  'Schwerstverletzten-Behandlungsplatz',
  'Transport',
  'Übergabe Notfallteam',
] as const;

export function formatTime(ms: number): string {
  const s = Math.floor(ms / 1000);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

export function describeEvent(
  event: Event,
  state: RunState,
  injectTitle: (id: string) => string,
): string {
  const unit = (id: string) => state.units[id]?.callsign ?? id;
  const patient = (id: string) => `Patient ${state.patients[id]?.number ?? id}`;
  switch (event.type) {
    case 'SignPlaced': {
      const label = event.label ?? signById(event.signType).label;
      // state is the state *before* this event: if the sign already exists, re-dispatching
      // SignPlaced with the same id is the rename trick (see RunScreen), not a new placement.
      return state.signs[event.signId] ? `${label} umbenannt` : `${label} platziert`;
    }
    case 'SignMoved':
      return `${state.signs[event.signId]?.label ?? event.signId} verschoben`;
    case 'SignRemoved':
      return `${state.signs[event.signId]?.label ?? unit(event.signId)} entfernt`;
    case 'UnitStatusChanged':
      return `${unit(event.unitId)}: ${UNIT_STATUS_LABELS[event.status]}`;
    case 'ReinforcementRequested':
      return `Nachforderung ${event.count}x ${signById(event.signType).label}${event.text ? `, ${event.text}` : ''}`;
    case 'ReinforcementArrived':
      return 'Nachforderung eingetroffen';
    case 'PatientTriaged': {
      const expected = state.patients[event.patientId]?.expectedCategory;
      const hit =
        expected === event.category ? '' : ` (Soll ${expected ? CATEGORY_LABELS[expected] : '?'})`;
      return `${patient(event.patientId)} gesichtet: ${CATEGORY_LABELS[event.category]}${hit}`;
    }
    case 'PatientTransferred':
      return `${patient(event.patientId)} verlegt nach ${event.destination}`;
    case 'SurgicalIndicationSet':
      return `${patient(event.patientId)}: akute OP-Indikation ${event.indicated ? 'ja' : 'nein'}`;
    case 'TransportReleaseSet':
      return `${patient(event.patientId)}: Transportfreigabe ${
        event.released ? 'erteilt' : 'nicht erteilt'
      }`;
    case 'PatientAdded':
      return `Patient ${event.patient.number} hinzugefügt`;
    case 'InjectTriggered':
      return `Einspieler: ${injectTitle(event.injectId)}`;
    case 'DrawingAdded':
      return `Zeichnung (${event.drawing.kind === 'line' ? 'Linie' : event.drawing.kind === 'polygon' ? 'Fläche' : 'Text'})`;
    case 'DrawingRemoved':
      return 'Zeichnung entfernt';
    case 'NoteAdded':
      return `Notiz: ${event.text}`;
  }
}
